import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Sort} from '@angular/material/sort';
import {BaseService, ConfigurationService, User, UUID} from '@nh/angular-tses';
import {environment} from 'environments/environment';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {UserGroup, UserGroupType} from '../../admin/admin-user-groups/user-group';

export interface OrganisationUser {
  userId: string;
  name: string;
  createdBy: string;
  createdAt: number;
}

export interface UserOrgMapping {
  id: string;
  userId: string;
  organisationId: string;
  createdAt: number;
  createdBy: string;
}

export interface Organisation extends UserGroup {
  readonly type: UserGroupType.ORGANISATION;
  obsolete: boolean;
}

export function groupIsOrganisation(group: UserGroup): group is Organisation {
  return group.type === UserGroupType.ORGANISATION;
}

export interface OrganisationResults {
  organisations: Organisation[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrganisationService extends BaseService {
  private static readonly ORGANISATIONS_ENDPOINT: string = `${environment.tsesConfig.apiHost}/organisations`;

  constructor(snackbar: MatSnackBar, configurationService: ConfigurationService, private http: HttpClient) {
    super(snackbar, configurationService);
  }

  public getOrganisation(organisationId: UUID): Observable<Organisation> {
    const idValue: string = organisationId.value;
    const url: string = `${OrganisationService.ORGANISATIONS_ENDPOINT}/organisations/${idValue}`;
    return this.http.get<Organisation>(url, {}).pipe(
      map(json => this.deserialise(json)),
      catchError((err: any) => {
        this._handleError(err, `Failed to get organisation with id: ${idValue}`);
        return of(null);
      })
    );
  }

  public getAllOrganisations(obsolete?: boolean): Observable<Organisation[]> {
    const url: string = `${OrganisationService.ORGANISATIONS_ENDPOINT}/`;
    const params: {[param: string]: string} = obsolete || obsolete === false ? {obsolete: String(obsolete)} : {};
    return this.http.get<Organisation[]>(url, {params}).pipe(
      map(json => this.deserialiseArray(json)),
      catchError((err: any) => {
        this._handleError(err, 'Failed to get organisations from database.');
        return of([]);
      })
    );
  }

  public searchOrganisations(
    searchQuery: string,
    page: number,
    pageSize: number,
    sort: Sort
  ): Observable<OrganisationResults> {
    const query = `${environment.tsesConfig.apiHost}/organisation-search/search/search`;
    const params: {[param: string]: string} = {
      search_query: searchQuery,
      page: !page ? '0' : String(page),
      size: !pageSize ? '10' : String(pageSize),
      sort: !sort ? null : sort.active + ',' + sort.direction
    };

    return this.http.get(query, {params}).pipe(
      map(json => this.mapJsonToOrganisationResults(json)),
      catchError((err: any) => {
        this._handleError(err, 'Failed to perform organisation search.');
        return of(null);
      })
    );
  }

  public getAUsersOrganisations(userId: UUID | string, obsolete?: boolean): Observable<Organisation[]> {
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/${userId}`;
    const params: {[param: string]: string} = obsolete || obsolete === false ? {obsolete: String(obsolete)} : {};

    return this.http.get<Organisation[]>(url, {params}).pipe(
      map(json => this.deserialiseArray(json)),
      catchError((err: any) => {
        this._handleError(err, `Failed to get this user's organisations.`);
        return of([]);
      })
    );
  }

  public setOrganisationObsolete(organisationId: UUID | string, obsolete: boolean): Observable<Organisation> {
    if (organisationId instanceof UUID) {
      organisationId = organisationId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/toggle-obsolete`;
    const params: {[param: string]: string} = {organisation: organisationId, obsolete: String(obsolete)};
    return this.http.patch<Organisation>(url, {}, {params}).pipe(
      map(json => this.deserialise(json)),
      catchError((err: any) => {
        this._handleError(err, 'Could not set organisation state.', 'organisations-error');
        return of(null);
      })
    );
  }

  public addOrganisation(organisationName: string): Observable<Organisation> {
    const url: string = `${OrganisationService.ORGANISATIONS_ENDPOINT}/${organisationName}`;
    return this.http.post<Organisation>(url, {}).pipe(
      map(json => {
        this.displaySnackbar(`Successfully added organisation: ${organisationName}`);
        return this.deserialise(json);
      }),
      catchError(err => {
        this._handleError(err, `Failed to add organisation with name ${organisationName}`, 'organisations-error');
        return of(null);
      })
    );
  }

  public getUsersByOrgId(orgId: UUID | string): Observable<User[]> {
    if (orgId instanceof UUID) {
      orgId = orgId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/users-in-org/${orgId}`;

    return this.http.get(url).pipe(map(json => this.mapToUserArray(json)));
  }

  public getUsersByNotOrgId(orgId: UUID | string): Observable<User[]> {
    if (orgId instanceof UUID) {
      orgId = orgId.value;
    }

    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/users-not-in-org/${orgId}`;

    return this.http.get(url).pipe(map(json => this.mapToUserArray(json)));
  }

  public addUserToOrganisation(
    organisationId: UUID | string,
    userId: UUID | string,
    successMessage?: string
  ): Observable<UserOrgMapping> {
    if (organisationId instanceof UUID) {
      organisationId = organisationId.value;
    }
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/${organisationId}/${userId}`;
    return this.http
      .post<UserOrgMapping>(url, {})
      .pipe(
        catchError(err =>
          throwError(this._handleError(err, `Failed to add user ${userId} to organisation ${organisationId}`))
        )
      );
  }

  public removeUserFromOrganisation(organisationId: UUID | string, userId: UUID | string): Observable<any> {
    if (organisationId instanceof UUID) {
      organisationId = organisationId.value;
    }
    if (userId instanceof UUID) {
      userId = userId.value;
    }

    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/organisation/${organisationId}/user/${userId}`;
    return this.http
      .delete(url, {})
      .pipe(
        catchError(err =>
          throwError(this._handleError(err, `Failed to remove user ${userId} from organisation ${organisationId}`))
        )
      );
  }

  public removeUserFromAllOrganisations(userId: UUID | string): Observable<any> {
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/user/${userId}`;
    return this.http.delete(url, {});
  }

  public removeAllUsersFromOrganisation(orgId: UUID | string): Observable<any> {
    if (orgId instanceof UUID) {
      orgId = orgId.value;
    }
    const url = `${OrganisationService.ORGANISATIONS_ENDPOINT}/organisation/${orgId}`;
    return this.http.delete(url, {});
  }

  private mapJsonToOrganisationResults(organisationsJson): OrganisationResults {
    const organisations: Organisation[] = this.deserialiseArray(organisationsJson._embedded.organisations);
    const total = organisationsJson.page.totalElements;
    return {organisations, total};
  }

  private displaySnackbar(message: string): void {
    this._snackbar.open(message, 'Dismiss');
  }

  private mapToUserArray(json): User[] {
    return json.map(user => User.deserialise(user));
  }

  private deserialise(json: any): Organisation {
    json.type = UserGroupType.ORGANISATION;
    return json as Organisation;
  }

  private deserialiseArray(json: any[]): Organisation[] {
    return json.map(j => this.deserialise(j));
  }
}
